import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import type { ReactNode } from "react"
import { useSearchParams } from "react-router-dom"
import { api } from "@/api.js"
import { Async } from "@/components/Async.js"
import { PageHeader, Stat, StatRow } from "@/components/Page.js"
import { Button } from "@/components/ui/button.js"
import { Input } from "@/components/ui/input.js"
import { LabeledSelectTrigger, Select, SelectContent, SelectItem } from "@/components/ui/select.js"
import { DecisionTable } from "./Entity.js"

const FILTERS = ["question", "decision", "degraded", "apiClientId", "code"] as const
type Filter = (typeof FILTERS)[number]

/**
 * For finding patterns rather than single cases.
 *
 * **The filters live in the URL** (2026-10-05), so a row on the Pulpit can
 * open exactly the decisions it counted, and a filtered list can be pasted
 * into a ticket as it stands.
 */
export function DecisionsScreen() {
  const [params, setParams] = useSearchParams()
  const filters = Object.fromEntries(FILTERS.map((key) => [key, params.get(key) ?? ""])) as Record<
    Filter,
    string
  >
  const setFilter = (key: Filter, value: string) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        if (value === "") next.delete(key)
        else next.set(key, value)
        return next
      },
      { replace: true },
    )
  const query = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""))
  // Keyset pages, appended: the audit log grows at the top, so a "load more"
  // walks backwards through it without rows shifting under the reader.
  const state = useInfiniteQuery({
    queryKey: ["decisions", query],
    queryFn: ({ pageParam }) => api.decisions(query, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
  // Offered by the registry the installed packages populate at startup, so a
  // provider added later appears here without this screen being edited.
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })

  const items = state.data?.pages.flatMap((page) => page.items) ?? []
  const prepayments = items.filter((item) => item.decision === "PREPAYMENT").length
  const degraded = items.filter((item) => item.degraded).length

  return (
    <div className="space-y-7">
      <PageHeader title="Decyzje" />

      <StatRow>
        <Stat label="W tym widoku" value={items.length} hint="Po nałożeniu filtrów poniżej." />
        <Stat
          label="Odroczenie"
          value={share(items.length - prepayments, items.length)}
          tone="allow"
          hint="Domyślna odpowiedź, gdy nic nie blokuje."
        />
        <Stat
          label="Przedpłata"
          value={share(prepayments, items.length)}
          tone="prepay"
          hint="Sprzedaż doszła do skutku, tylko z góry."
        />
        <Stat
          label="Niepewne"
          value={share(degraded, items.length)}
          tone={degraded > 0 ? "critical" : "neutral"}
          hint="Odpowiedzi z fail-closed, nie z kompletnych danych."
        />
      </StatRow>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={filters.question === "" ? "any" : filters.question}
            onValueChange={(value) => setFilter("question", value === "any" ? "" : value)}
          >
            <LabeledSelectTrigger label="Pytanie" />
            <SelectContent>
              <SelectItem value="any">dowolne</SelectItem>
              {(registry.data?.questions ?? []).map((question) => (
                <SelectItem key={question.questionId} value={question.questionId}>
                  {question.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.decision === "" ? "any" : filters.decision}
            onValueChange={(value) => setFilter("decision", value === "any" ? "" : value)}
          >
            <LabeledSelectTrigger label="Decyzja" />
            <SelectContent>
              <SelectItem value="any">dowolna</SelectItem>
              <SelectItem value="ALLOW">odroczenie</SelectItem>
              <SelectItem value="PREPAYMENT">przedpłata</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.degraded === "" ? "any" : filters.degraded}
            onValueChange={(value) => setFilter("degraded", value === "any" ? "" : value)}
          >
            <LabeledSelectTrigger label="Pewność" />
            <SelectContent>
              <SelectItem value="any">dowolna</SelectItem>
              <SelectItem value="true">tylko niepewne</SelectItem>
              <SelectItem value="false">tylko z pełnych danych</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.code === "" ? "any" : filters.code}
            onValueChange={(value) => setFilter("code", value === "any" ? "" : value)}
          >
            <LabeledSelectTrigger label="Kod powodu" />
            <SelectContent>
              <SelectItem value="any">dowolny</SelectItem>
              {(registry.data?.reasonCodes ?? []).map((reason) => (
                <SelectItem key={reason.code} value={reason.code}>
                  <span className="font-mono text-[12.5px]">{reason.code}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            className="w-44"
            value={filters.apiClientId}
            placeholder="Konsument…"
            aria-label="Konsument"
            onChange={(event) => setFilter("apiClientId", event.target.value)}
          />
        </div>

        <Async state={state}>
          {() => (
            <div className="space-y-4">
              <DecisionTable decisions={items} />
              {state.hasNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => void state.fetchNextPage()}
                    disabled={state.isFetchingNextPage}
                  >
                    {state.isFetchingNextPage ? "Wczytywanie…" : "Pokaż więcej"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Async>
      </div>
    </div>
  )
}

function share(count: number, total: number): ReactNode {
  if (total === 0) return "—"
  return (
    <>
      {count}
      <span className="ml-1.5 text-[15px] font-normal text-muted-foreground">
        · {Math.round((count / total) * 100)}%
      </span>
    </>
  )
}
